import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

interface HivePadNotificationCounts {
  overdue: number;
  incomplete: number;
  total: number;
}

export const useHivePadNotifications = () => {
  const { organizationId } = useAuth();

  const { data: counts = { overdue: 0, incomplete: 0, total: 0 }, isLoading } = useQuery({
    queryKey: ['hivePadNotifications', organizationId],
    queryFn: async (): Promise<HivePadNotificationCounts> => {
      if (!organizationId) {
        return { overdue: 0, incomplete: 0, total: 0 };
      }

      try {
        // Get all active (non-archived) notes
        const { data: notes, error } = await supabase
          .from('hive_notes')
          .select('completed, due_date')
          .eq('organization_id', organizationId)
          .eq('archived', false);

        if (error) throw error;

        const today = new Date();
        today.setHours(0, 0, 0, 0); // Start of today

        let overdue = 0;
        let incomplete = 0;

        notes?.forEach(note => {
          if (!note.completed) {
            incomplete++;
            
            // Check if overdue (has due date and it's past today)
            if (note.due_date) {
              const dueDate = new Date(note.due_date);
              dueDate.setHours(0, 0, 0, 0);
              
              if (dueDate < today) {
                overdue++;
              }
            }
          }
        });

        return {
          overdue,
          incomplete,
          total: Math.max(overdue, incomplete) // Show the higher count as priority
        };
      } catch (error) {
        console.error('Error fetching HivePad notifications:', error);
        return { overdue: 0, incomplete: 0, total: 0 };
      }
    },
    enabled: !!organizationId,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Refetch every minute
  });

  return {
    counts,
    isLoading,
    hasNotifications: counts.total > 0,
    hasOverdue: counts.overdue > 0,
  };
};